import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Box, Drawer } from '@mui/material';
import { toast } from 'react-toastify';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

// components
import ProjectAddTask from './ProjectAddTask';

import { isAdmin, isStakeholder } from '../../../../../utils/share';
import { getAllTasksByProjectId, getTaskById } from '../../../../../services/taskService';
import { TASK_LIST_TABLE_COLUMNS, TASK_LIST_TABLE_DEFAULT_PROPS } from './constants';
import DeleteModal from './DeleteModal';
import TopToolBar from './TopToolBar';
import Actions from './Actions';
import TableWrapper from '../../../../../common/TableWrapper';

const ProjectTaskListTable = ({ projectName }) => {
  const { id } = useParams(); // Extracting project ID from URL parameters

  // State variables to manage the component's behavior
  const [action, setAction] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState();
  const [deleteSelected, setDeleteSelected] = useState();

  // Variables for managing the table data asynchronously
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // handles the asynchronous api calls
  const {
    data: { count, rows } = { count: 0, rows: [] },
    isError,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      try {
        const response = await getAllTasksByProjectId({
          start: pagination.pageIndex * pagination.pageSize,
          pageSize: pagination.pageSize,
          projectId: id,
          filters: JSON.stringify(columnFilters ?? []),
          globalFilter,
          sorting: JSON.stringify(sorting ?? []),
        });
        return response?.data;
      } catch (error) {
        toast.error('An error occurred while fetching the task list. Please try again later.');
      }
      return { count: 0, rows: [] };
    },
    placeholderData: keepPreviousData,
  });

  const onDeleteIconClick = (data) => {
    if (parseInt(data?.consumedHours, 10) <= 0) {
      setDeleteSelected(data);
    }
  };

  // Handling task deletion
  const onTaskDelete = () => {
    try {
      setDeleteSelected(undefined);
      refetch();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? error.message);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteSelected(undefined);
  };

  // Handling task creation
  const handleCreate = () => {
    setAction('create');
    handleDrawerOpen();
  };

  // Opening the drawer
  const handleDrawerOpen = () => {
    setIsOpen(true);
  };

  const handleEditCellClick = async (task) => {
    setAction('update');
    try {
      const response = await getTaskById(task.id);
      const taskData = response?.data?.data;
      if (taskData) {
        const updatedTaskData = {
          ...taskData,
          consumedHours: task.consumedHours,
        };
        setSelected(updatedTaskData);
        handleDrawerOpen();
      } else {
        toast.error('Task data not found');
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Closing the drawer and resetting selected task
  const handleDrawerClose = () => {
    setSelected(null);
    setIsOpen(false);
  };

  // Configuration for CSV export

  // Define columns
  const columns = useMemo(() => TASK_LIST_TABLE_COLUMNS, []);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Error loading data',
        }
      : undefined,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    enableRowActions: isAdmin(),
    rowCount: count ?? 0,
    renderRowActions: ({ row }) => <Actions row={row} onDeleteIconClick={onDeleteIconClick} />,
    renderTopToolbarCustomActions: () => <TopToolBar onCreateClick={handleCreate} />,
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: '#fff', cursor: 'pointer' },
      onClick: () => {
        handleEditCellClick(row.original);
      },
    }),
    ...TASK_LIST_TABLE_DEFAULT_PROPS,
  });

  return (
    <>
      <TableWrapper>
        <MaterialReactTable table={table} />
      </TableWrapper>
      <DeleteModal deleteSelected={deleteSelected} onDelete={onTaskDelete} onClose={handleDeleteCancel} />
      {!isStakeholder() && (
        <Drawer
          PaperProps={{
            sx: {
              width: '55%',
            },
          }}
          anchor="right"
          open={isOpen}
          onClose={handleDrawerClose}
        >
          <Box p={2}>
            <ProjectAddTask
              selectedTask={selected}
              open={isOpen}
              close={handleDrawerClose}
              onAddTask={handleCreate}
              action={action}
              onSubmit={refetch}
              projectName={projectName}
            />
          </Box>
        </Drawer>
      )}
    </>
  );
};

ProjectTaskListTable.propTypes = {
  projectName: PropTypes.string,
};

export default ProjectTaskListTable;
